import {
  BeekLogo,
  DeviceSizes,
  InputForm,
  Typography,
} from 'beek-design-system';
import { Button } from '@beek/design-system';
import { FormEvent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';
import toast from 'react-hot-toast';

import { BeekUserRole } from 'src/utils/user';
import { getUserProfile, getUserSubscriptionInfo } from 'src/api/user/dataUser';
import { setUserRole } from 'src/app/auth/setUserInfo';
import Avo from 'src/tracking/Avo';
import Const from 'src/config/Const';
import Link from 'next/link';
import authenticateUserWithBeek from 'src/app/auth/authenticateUserWithBeek';
import emailRegex from 'src/utils/emailRegex';
import pathWithQueryString from 'src/common/utils/pathWithQueryString';
import selectExperiment from 'src/common/utils/selectExperiment';

const BeekLogoExtended = styled(BeekLogo)`
  width: 100%;
  margin-top: 1.5rem;
  margin-bottom: 3.0625rem;
  padding-left: 1rem;
  @media ${DeviceSizes.laptop} {
    width: 57.25rem;
    margin-bottom: 4rem;
    padding-left: 0rem;
  }
`;

const LogInContainer = styled.form`
  border: none;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  margin-bottom: 7.438rem;
  align-self: center;
  padding-right: 1rem;
  padding-left: 1rem;
  @media ${DeviceSizes.laptop} {
    border: 0.063rem solid var(--gray-15);
    border-radius: 0.5rem;
    width: 22.25rem;
    height: 32.938rem;
    margin-bottom: 4.063rem;
    padding-right: 1.125rem;
  }
`;

const LogInTitle = styled(Typography)`
  margin-bottom: 2rem;
  align-self: center;
  @media ${DeviceSizes.laptop} {
    margin-top: 3rem;
    margin-bottom: 3rem;
  }
`;

const PasswordContainer = styled.div`
  display: flex;
  margin-bottom: 0.25rem;
  justify-content: space-between;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: stretch;

  margin-bottom: 1.5rem;
  @media ${DeviceSizes.laptop} {
    margin-bottom: 1.375rem;
  }
`;

const CreateAccountContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const LoginForm = (): JSX.Element => {
  const [inputPasswordType, setInputPasswordType] = useState('password');
  const [routesExperiment, setRoutesExperiment] = useState(Const.beekApiLogin);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formIsValid, setFormIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const t = useTranslations('auth');
  const router = useRouter();

  const { offer, redirect_uri } = router.query;

  const login = async (): Promise<void> => {
    setIsLoading(true);

    const payload = {
      email,
      password,
    };

    try {
      await authenticateUserWithBeek(routesExperiment, payload);

      const [userInfo, userProfile] = await Promise.all([
        getUserSubscriptionInfo(),
        getUserProfile(),
      ]);

      const hasSubscription = userInfo?.hasSubscription;
      setUserRole(userProfile.role ?? BeekUserRole.user);

      if (typeof redirect_uri === 'string') {
        router.push(decodeURIComponent(redirect_uri));
      } else if (hasSubscription) {
        router.push('/home');
      } else {
        const redirect_path = pathWithQueryString('/subscription/start', {
          offer,
        });
        router.push(redirect_path);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message: string = error?.message as string;
      const authWordRegex = new RegExp(/\b([Aa]utorizado|[Aa]uthorized){1}/);
      if (authWordRegex.test(message)) {
        toast.error(t('errors.notAuthorized'));
      } else {
        toast.error(error.message);
      }
      setIsLoading(false);
    }
  };

  const handleSignupClick = (): void => {
    Avo.signupFlowStarted({
      origin: 'Login Screen - "Already Have Account?" Link',
    });
  };

  const handleSubmit = (
    e?: FormEvent<HTMLButtonElement | HTMLAnchorElement | HTMLFormElement>
  ): void => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (formIsValid) login();
  };

  useEffect(() => {
    const renderExperiment = async () => {
      const experiment = await selectExperiment(
        Const.routesExperiment,
        Const.beekApiLogin,
        Const.beekAuthLogin
      );
      setRoutesExperiment(experiment);
    };
    renderExperiment();
  }, []);

  useEffect(() => {
    if (emailRegex.test(email) && password.length > 5) {
      setFormIsValid(true);
    } else {
      setFormIsValid(false);
    }
  }, [email, password]);

  return (
    <>
      <BeekLogoExtended />
      <LogInContainer onSubmit={handleSubmit}>
        <LogInTitle type="headline3">{t('login.title') as string}</LogInTitle>
        <Typography type="caption" marginBottom="0.25rem">
          {t('login.email') as string}
        </Typography>
        <InputForm
          marginBottom="2.5rem"
          type="email"
          name="email"
          isValid={emailRegex.test(email) || email.length === 0}
          setInputValue={setEmail}
          labelError={t('errors.email') as string}
          value={email}
        />
        <PasswordContainer>
          <Typography type="caption">
            {t('login.password') as string}
          </Typography>
          <Link href="/password/reset" tabIndex={-1}>
            <Typography type="caption" textDecoration="underline">
              {t('login.forgotPassword') as string}
            </Typography>
          </Link>
        </PasswordContainer>
        <InputForm
          marginBottom="3rem"
          type={inputPasswordType}
          setInputPasswordType={setInputPasswordType}
          name="password"
          isValid={password.length > 5 || password.length === 0}
          setInputValue={setPassword}
          labelError={t('errors.password') as string}
          value={password}
        />
        <ButtonContainer>
          <Button
            onClick={formIsValid ? handleSubmit : undefined}
            loading={isLoading}
            disabled={!formIsValid}
          >
            {t('login.loginButton') as string}
          </Button>
        </ButtonContainer>
        <CreateAccountContainer>
          <Typography type="body2" marginRight="0.391rem">
            {t('login.dontHaveAccount') as string}
          </Typography>
          <Link
            href={pathWithQueryString('/signup', { offer })}
            onClick={handleSignupClick}
          >
            <a>
              <Typography textDecoration="underline" type="body2">
                {t('login.createAccount') as string}
              </Typography>
            </a>
          </Link>
        </CreateAccountContainer>
      </LogInContainer>
    </>
  );
};

export default LoginForm;
